import { Extension } from "@tiptap/core"; // Removed JSONContent import
import { EditorState } from "@tiptap/pm/state";
import { EditorView } from "@tiptap/pm/view";

import { findNodeWithPosition } from "@/editor/utils/nodes";
import { useAnalyticsService } from "@/services/analyticsService";
import { ErrorService } from "@/services/errorService";

import { askAsync, examplesAsync, explainAsync, simplifyAsync } from "./queries";
import { convertStringToJSONContent } from "./utils";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    ai: {
      /**
       * Execute async AI simplification for the given highlight context
       */
      simplify: (group: string, highlightContext: string, surroundingContext: string, noteId: string) => ReturnType;

      /**
       * Execute async AI explanation for the given highlight context
       */
      explain: (group: string, highlightContext: string, surroundingContext: string, noteId: string) => ReturnType;

      /**
       * Execute async AI examples for the given highlight context
       */
      examples: (group: string, highlightContext: string, surroundingContext: string, noteId: string) => ReturnType;

      /**
       * Execute async AI ask for the given highlight context
       */
      ask: (
        group: string,
        highlightContext: string,
        surroundingContext: string,
        noteId: string,
        query: string,
      ) => ReturnType;
    };
  }
}

export const AI = Extension.create({
  name: "ai",

  addCommands() {
    const trackAIAction = (
      action: string,
      group: string,
      highlightContext: string,
      surroundingContext: string,
      result: string,
      query?: string,
    ) => {
      if (action === "simplify") {
        useAnalyticsService().trackEditorAIAction("simplify", group, highlightContext, surroundingContext, result);
      } else if (action === "explain") {
        useAnalyticsService().trackEditorAIAction("explain", group, highlightContext, surroundingContext, result);
      } else if (action === "examples") {
        useAnalyticsService().trackEditorAIAction("examples", group, highlightContext, surroundingContext, result);
      } else if (action === "ask") {
        useAnalyticsService().trackEditorAIAction("ask", group, highlightContext, surroundingContext, result, query);
      }
    };

    const updateNoteContent = (
      state: EditorState,
      view: EditorView,
      noteId: string,
      result: string | undefined,
      error: string | undefined = undefined,
    ): boolean => {
      const nodeInfo = findNodeWithPosition(state, "nw-note", noteId);

      if (!nodeInfo) {
        console.error("Node not found for noteId:", noteId);
        return false;
      }

      const { pos } = nodeInfo;
      const tr = state.tr;

      if (error) {
        tr.setNodeAttribute(pos, "loading", false);
        tr.setNodeAttribute(pos, "error", "Something went wrong, please try again.");
        view.dispatch(tr.setMeta("addToHistory", false));
        ErrorService.report(error);
      } else if (result !== undefined) {
        const jsonContent = convertStringToJSONContent(result, state.schema);

        if (jsonContent) {
          tr.setNodeAttribute(pos, "content", jsonContent);
          tr.setNodeAttribute(pos, "loading", false);
          tr.setNodeAttribute(pos, "error", null);
          view.dispatch(tr.setMeta("addToHistory", false));
        } else {
          // Handle case where conversion to JSONContent failed
          tr.setNodeAttribute(pos, "loading", false);
          tr.setNodeAttribute(pos, "error", "Failed to process AI response format.");
          view.dispatch(tr.setMeta("addToHistory", false));
          ErrorService.report(new Error(`Failed to convert AI response to JSONContent for note ${noteId}`));
        }
      } else {
        // Handle case where result is undefined but no error occurred
        tr.setNodeAttribute(pos, "loading", false);
        tr.setNodeAttribute(pos, "content", null); // Or { type: 'doc', content: [] }
        view.dispatch(tr.setMeta("addToHistory", false));
      }

      return true;
    };

    return {
      simplify:
        (group, highlightContext, surroundingContext, noteId) =>
        ({ state, view }) => {
          simplifyAsync(highlightContext)
            .then((result: string) => {
              trackAIAction("simplify", group, highlightContext, surroundingContext, result);
              return updateNoteContent(state, view, noteId, result, undefined);
            })
            .catch(error => {
              return updateNoteContent(state, view, noteId, undefined, error);
            });

          return true;
        },
      explain:
        (group, highlightContext, surroundingContext, noteId) =>
        ({ state, view }) => {
          explainAsync(highlightContext)
            .then((result: string) => {
              trackAIAction("explain", group, highlightContext, surroundingContext, result);
              return updateNoteContent(state, view, noteId, result, undefined);
            })
            .catch(error => {
              return updateNoteContent(state, view, noteId, undefined, error);
            });

          return true;
        },
      examples:
        (group, highlightContext, surroundingContext, noteId) =>
        ({ state, view }) => {
          examplesAsync(highlightContext)
            .then((result: string) => {
              trackAIAction("examples", group, highlightContext, surroundingContext, result);
              return updateNoteContent(state, view, noteId, result, undefined);
            })
            .catch(error => {
              return updateNoteContent(state, view, noteId, undefined, error);
            });

          return true;
        },
      ask:
        (group, highlightContext, surroundingContext, noteId, query) =>
        ({ state, view }) => {
          askAsync(highlightContext, query)
            .then((result: string) => {
              trackAIAction("ask", group, highlightContext, surroundingContext, result, query);
              return updateNoteContent(state, view, noteId, result, undefined);
            })
            .catch(error => {
              return updateNoteContent(state, view, noteId, undefined, error);
            });

          return true;
        },
    };
  },
});
